import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useLang } from "@/contexts/LanguageContext";
import { api, ACTIVITIES_PRIMARY } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Slider } from "@/components/ui/slider";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { Plus, Users, Loader2, MessageCircle, ChevronRight, Hourglass } from "lucide-react";
import Avatar from "@/components/Avatar";
import { toast } from "sonner";

export default function Groups() {
  const { t } = useLang();
  const nav = useNavigate();
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", description: "", activity: "", max_members: 8 });

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.get("/groups");
      setGroups(res.data);
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/groups", form);
      toast.success(t("saved"));
      setOpen(false);
      setForm({ name: "", description: "", activity: "", max_members: 8 });
      load();
    } catch (err) {
      toast.error(t("networkErr"));
    }
  };

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 py-8" data-testid="groups-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-[hsl(var(--secondary))] font-bold">{t("groups")}</div>
          <h1 className="mt-1 text-3xl font-heading font-semibold tracking-tight">{t("groups")}</h1>
          <p className="mt-1 text-sm text-[hsl(var(--muted-foreground))]">Tap a group to open its chat.</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="create-group-btn" className="rounded-full bg-[hsl(var(--primary))] hover:bg-[hsl(var(--primary-hover))] text-white hover:-translate-y-0.5 transition-all">
              <Plus className="h-4 w-4 me-1.5" /> {t("createGroup")}
            </Button>
          </DialogTrigger>
          <DialogContent className="rounded-3xl">
            <DialogHeader>
              <DialogTitle>{t("createGroup")}</DialogTitle>
              <DialogDescription>{t("description")}</DialogDescription>
            </DialogHeader>
            <form onSubmit={submit} className="space-y-4">
              <div>
                <Label>{t("groupName")}</Label>
                <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required className="mt-1.5 rounded-xl h-11" data-testid="grp-name-input" />
              </div>
              <div>
                <Label>{t("description")}</Label>
                <Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="mt-1.5 rounded-xl" rows={2} data-testid="grp-desc-input" />
              </div>
              <div>
                <Label>{t("activity")}</Label>
                <div className="mt-2 grid grid-cols-2 sm:grid-cols-3 gap-2">
                  {ACTIVITIES_PRIMARY.map((a) => (
                    <button
                      key={a}
                      type="button"
                      onClick={() => setForm({ ...form, activity: a })}
                      data-testid={`grp-act-${a.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                      className={`px-3 py-2 rounded-xl text-sm border transition-all ${
                        form.activity === a
                          ? "bg-[hsl(var(--primary))] text-white border-[hsl(var(--primary))]"
                          : "bg-white border-[hsl(var(--border))] hover:border-[hsl(var(--primary))]"
                      }`}
                    >
                      {a}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <Label>{t("maxMembers")}: <span className="font-semibold text-[hsl(var(--primary))]">{form.max_members}</span></Label>
                <Slider className="mt-3" value={[form.max_members]} min={2} max={10} step={1} onValueChange={(v) => setForm({ ...form, max_members: v[0] })} />
              </div>
              <DialogFooter>
                <Button type="submit" data-testid="grp-submit" className="rounded-full bg-[hsl(var(--primary))] hover:bg-[hsl(var(--primary-hover))] text-white">
                  {t("createGroup")}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {loading ? (
        <div className="flex justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-[hsl(var(--primary))]" /></div>
      ) : groups.length === 0 ? (
        <div className="bg-white border border-dashed border-[hsl(var(--border))] rounded-3xl p-12 text-center text-[hsl(var(--muted-foreground))]" data-testid="groups-empty">
          <Users className="h-8 w-8 mx-auto text-[hsl(var(--primary))] mb-3" />
          {t("noGroups")}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          {groups.map((g) => (
            <button
              key={g.id}
              onClick={() => nav(`/groups/${g.id}`)}
              data-testid={`group-card-${g.id}`}
              className="text-start bg-white border border-[hsl(var(--border))] rounded-3xl p-5 hover:-translate-y-1 hover:border-[hsl(var(--primary))] transition-all shadow-[0_4px_24px_rgba(31,30,27,0.04)] group"
            >
              <div className="flex items-start gap-4">
                <div className="relative shrink-0">
                  <div className="h-14 w-14 rounded-2xl bg-[hsl(var(--primary))] text-white flex items-center justify-center font-heading text-xl font-semibold shadow-[0_4px_18px_rgba(22,200,90,0.3)]">
                    {g.name?.slice(0, 1).toUpperCase()}
                  </div>
                  <span className="absolute -bottom-1 -end-1 h-6 w-6 rounded-full bg-[hsl(var(--secondary))] border-2 border-white flex items-center justify-center">
                    <MessageCircle className="h-3 w-3 text-white" />
                  </span>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <div className="font-heading text-lg font-semibold truncate">{g.name}</div>
                      {g.activity && <div className="text-xs text-[hsl(var(--secondary))] uppercase tracking-wider font-bold mt-0.5">{g.activity}</div>}
                    </div>
                    <div className="flex flex-col items-end gap-1 shrink-0">
                      <span className="text-xs px-2.5 py-1 rounded-full bg-[hsl(var(--elevated))] border border-[hsl(var(--border))]">
                        {g.member_ids.length}/{g.max_members}
                      </span>
                      {g.pending_invites_count > 0 && (
                        <span
                          data-testid={`pending-invites-${g.id}`}
                          className="inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full bg-[hsl(var(--primary))]/10 text-[hsl(var(--primary))] border border-[hsl(var(--primary))]/30 font-semibold uppercase tracking-wider"
                          title={`${g.pending_invites_count} ${t("pending")}`}
                        >
                          <Hourglass className="h-3 w-3" />
                          {g.pending_invites_count} {t("pending")}
                        </span>
                      )}
                    </div>
                  </div>
                  {g.description && <div className="mt-2 text-sm text-[hsl(var(--muted-foreground))] line-clamp-2">{g.description}</div>}

                  <div className="mt-4 flex items-center justify-between">
                    <div className="flex -space-x-2">
                      {(g.members || []).slice(0, 5).map((m) => (
                        <Avatar key={m.id} user={m} size={28} ring />
                      ))}
                      {g.members?.length > 5 && (
                        <div className="h-7 w-7 rounded-full bg-[hsl(var(--elevated))] text-[hsl(var(--foreground))] flex items-center justify-center text-[11px] font-semibold border-2 border-white">
                          +{g.members.length - 5}
                        </div>
                      )}
                    </div>
                    <span className="inline-flex items-center gap-1.5 text-sm font-medium text-[hsl(var(--primary))] group-hover:translate-x-0.5 transition-transform" data-testid={`open-chat-${g.id}`}>
                      <MessageCircle className="h-4 w-4" />
                      Open chat
                      <ChevronRight className="h-4 w-4 rtl:rotate-180" />
                    </span>
                  </div>
                </div>
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
